import React from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import api from "../../service/api";

/**
 * Download de vários PDFs de uma vez.
 *
 * O lote sai do MESMO filtro da listagem (formulário + período + texto): o
 * usuário baixa exatamente o que está vendo na tela. O backend devolve um único
 * arquivo com um preenchimento por página (ver PdfController::batch no
 * bpf-rest-api) — não é uma fila, a geração é síncrona e limitada, então
 * mostramos a contagem antes de deixar o botão disponível.
 *
 * A requisição vai por XHR (e não window.open) porque a rota é autenticada:
 * é o `api` que carrega o Bearer. O arquivo é montado a partir do blob.
 */
export default class DialogBatchPdf extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      counting: true,
      generating: false,
      count: null,
      max: null,
      error: "",
      orientation: "portrait",
    };
  }

  componentDidMount() {
    this.getCount();
  }

  getCount = () => {
    this.setState({ counting: true, error: "" });

    api
      .get("/data-form/pdf-batch/count", {
        params: {
          form_id: this.props.form_id,
          fromDate: this.props.fromDate,
          toDate: this.props.toDate,
          data: this.props.data_filter || null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      })
      .then((response) => {
        // Em erro de rede o interceptor de api.js resolve com undefined em vez
        // de rejeitar; sem esta guarda o .then quebraria ao ler .data.
        if (!response) {
          this.setState({ counting: false, error: "Falha de comunicacao com o servidor." });
          return;
        }
        this.setState({
          counting: false,
          count: response.data.count,
          max: response.data.max,
        });
      })
      .catch(() => {
        this.setState({
          counting: false,
          error: "Nao foi possivel contar os preenchimentos do periodo.",
        });
      });
  };

  handleChangeOrientation = (event) => {
    this.setState({ orientation: event.target.value });
  };

  // O nome do arquivo vem do Content-Disposition quando o servidor manda um;
  // sem ele, cai num nome derivado do periodo filtrado.
  resolveFileName = (response) => {
    const disposition = response.headers && response.headers["content-disposition"];
    const match = disposition && disposition.match(/filename="?([^";]+)"?/i);

    if (match) {
      return match[1];
    }

    return `formularios-${this.props.fromDate}-a-${this.props.toDate}.pdf`;
  };

  handleDownload = () => {
    this.setState({ generating: true, error: "" });

    api
      .post(
        "/data-form/pdf-batch",
        {
          form_id: this.props.form_id,
          fromDate: this.props.fromDate,
          toDate: this.props.toDate,
          data: this.props.data_filter || null,
          pgOrientation: this.state.orientation,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        { responseType: "blob" }
      )
      .then((response) => {
        if (!response) {
          this.setState({
            generating: false,
            error: "Falha de comunicacao com o servidor.",
          });
          return;
        }

        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", this.resolveFileName(response));
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.setState({ generating: false });
        this.props.handleClose();
      })
      .catch(() => {
        this.setState({
          generating: false,
          error: "Nao foi possivel gerar os PDFs. Reduza o periodo e tente novamente.",
        });
      });
  };

  renderBody = () => {
    const { counting, count, max, error } = this.state;

    if (counting) {
      return (
        <DialogContentText>
          <CircularProgress size={18} style={{ marginRight: 8 }} />
          Contando preenchimentos do periodo...
        </DialogContentText>
      );
    }

    if (error) {
      return <DialogContentText style={{ color: "#c62828" }}>{error}</DialogContentText>;
    }

    if (count === 0) {
      return (
        <DialogContentText>
          Nenhum preenchimento no periodo selecionado.
        </DialogContentText>
      );
    }

    if (count > max) {
      return (
        <DialogContentText style={{ color: "#c62828" }}>
          O filtro atual tem {count} preenchimentos e o limite por download e de {max}.
          Reduza o periodo ou refine o filtro.
        </DialogContentText>
      );
    }

    return (
      <div>
        <DialogContentText>
          {count} preenchimento(s) serao gerados em um unico PDF, um por pagina.
        </DialogContentText>
        <FormControl component="fieldset">
          <FormLabel component="legend">Orientacao</FormLabel>
          <RadioGroup
            row
            name="orientation"
            value={this.state.orientation}
            onChange={this.handleChangeOrientation}
          >
            <FormControlLabel value="portrait" control={<Radio />} label="Vertical" />
            <FormControlLabel value="landscape" control={<Radio />} label="Horizontal" />
          </RadioGroup>
        </FormControl>
      </div>
    );
  };

  render() {
    const { counting, generating, count, max, error } = this.state;
    const canDownload =
      !counting && !generating && !error && count > 0 && count <= max;

    return (
      <Dialog open={true} aria-labelledby="batch-pdf-title" maxWidth="xs" fullWidth>
        <DialogTitle
          style={{ backgroundColor: "#023575", color: "#fff", textAlign: "center" }}
          id="batch-pdf-title"
        >
          Baixar PDFs do periodo
        </DialogTitle>
        <DialogContent>{this.renderBody()}</DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            disabled={generating}
            onClick={this.props.handleClose}
          >
            Fechar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!canDownload}
            onClick={this.handleDownload}
          >
            {generating && (
              <CircularProgress size={20} style={{ color: "#fff", marginRight: 8 }} />
            )}
            {generating ? "Gerando..." : "Baixar"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
